import { chain, isEmpty } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import DataTable from "PFComponents/data_table/data_table";
import { LoadingDots } from "PFComponents/loading_dots";
import NotFoundComponent from "PFComponents/not_found/not_found";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { Typography } from "PFComponents/typography";
import { useDeletePermissionsGroup } from "PFCore/hooks/queries/admin/use_delete_permissions_group";
import { usePermissionsGroups } from "PFCore/hooks/queries/admin/use_permissions_groups";
import AdminListPage from "PFModules/admin/components/admin_list_page";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./permissions_groups_page.module.scss";

export const PermissionsGroupsPage = () => {
  const { t } = useTranslation("admin", { keyPrefix: "permissionsGroups" });
  const growl = useGrowl();
  const [state, setState] = useState({
    displayRemoveConfirm: false
  });

  const [page, setPage] = useState(1);

  const { data: permissionsGroups, isLoading, isError, isFetching } = usePermissionsGroups(page);
  const { mutate: deletePermissionsGroup } = useDeletePermissionsGroup();

  const handleRemove = (pg) => {
    setState({ displayRemoveConfirm: true, pgToBeRemoved: pg });
  };

  const handleRemoveOK = () => {
    const { pgToBeRemoved } = state;

    deletePermissionsGroup(pgToBeRemoved.id, {
      onError: (xhr) => {
        chain(xhr.responseJSON.errors)
          .values()
          .flatten()
          .value()
          .forEach((err) => {
            growl({ kind: "error", message: err.detail });
          });
      }
    });
  };

  const handleRemoveClose = () => {
    setState({ displayRemoveConfirm: false, idToBeRemoved: null });
  };

  const renderButtons = (pg) => (
    <div className={css.listButtonsWrapper}>
      <ButtonLink small kind="secondary" href={`/admin/permissions_groups/${pg.id}/edit`}>
        {t("translation:edit")}
      </ButtonLink>
      <Button small kind="danger" disabled={pg.default} onClick={() => handleRemove(pg)}>
        {t("translation:remove")}
      </Button>
    </div>
  );

  const { displayRemoveConfirm, pgToBeRemoved } = state;

  if (isLoading) {
    return <LoadingDots circlesEnabled pageViewEnabled />;
  }

  if (isEmpty(permissionsGroups) || isError) {
    return <NotFoundComponent message={t("noGroups")} />;
  }

  return (
    <React.Fragment>
      <AdminListPage
        title={t("title")}
        pagination={
          <SimplePagination
            disabled={isFetching}
            currentPage={page}
            totalPages={Math.ceil((permissionsGroups.meta?.total || 0) / 25)}
            handleClick={setPage}
          />
        }
        actions={<ButtonLink href="/admin/permissions_groups/new">{t("newPermissionGroup")}</ButtonLink>}
      >
        <DataTable
          columns={[
            { headerName: t("columns.displayAs"), cellKey: "display_as" },
            { headerName: t("columns.name"), cellKey: "name" },
            {
              headerName: t("columns.default"),
              renderCell: (pg) => t(`translation:${pg.default ? "yes" : "no"}`)
            },
            { headerName: t("columns.actions"), narrow: true, renderCell: renderButtons }
          ]}
          data={permissionsGroups?.entries}
        />
      </AdminListPage>
      {displayRemoveConfirm && (
        <ConfirmModal handleOK={handleRemoveOK} handleClose={handleRemoveClose}>
          {/* TODO: [PROF-5801] Remove padding-right */}
          <Typography variant="bodyRegular" className={css.confirmModalText} noMargin>
            {t("removeConfirmation", { permissionsGroupName: pgToBeRemoved.name })}
          </Typography>
        </ConfirmModal>
      )}
    </React.Fragment>
  );
};
